// File: /js/shared/context/requestContext.js

/**
 * Build the AJAX request context from URL/DOM state.
 *
 * Optional `el` argument enables a **scope-hash override**: when the
 * originating element is nested inside `[data-scope-hash="X"]`, the
 * context's `hash` and `content_hash` are X instead of the URL's hash.
 *
 * This is the single seam that lets inline embedded editors (today:
 * associated social cards on a source content's edit page) dispatch
 * AJAX through the embedded content's pipeline instead of the page's
 * primary content. Every AJAX handler — click, oninput, change,
 * upload, submit — calls this function, so the override applies
 * uniformly across all event types. No per-handler scope-hash code.
 *
 * Precedence inside callers is `{...buildContext(el), ...el.dataset}`,
 * so an explicit `data-hash` on the action element still wins (used by
 * the social-card delete button to dispatch against the SOURCE's hash
 * even from inside a card scope).
 */
function buildContext(el) {
  const params = new URLSearchParams(window.location.search);

  // URLs use ?hash=... everywhere; that's the default.
  let hash = params.get('hash') || undefined;

  // Scope-hash override: walk up from the originating element. If any
  // ancestor declares `data-scope-hash="X"`, X becomes the request hash.
  if (el instanceof Element) {
    const scopeEl = el.closest('[data-scope-hash]');
    if (scopeEl && scopeEl.dataset.scopeHash) {
      hash = scopeEl.dataset.scopeHash;
    }
  }

  const ctx = {
    hash,
    content_hash: hash,           // forward-compatible alias
    page: document.body.dataset.page || undefined,
    currentUrl: window.location.pathname + window.location.search
  };

  // strip out undefined / null
  Object.keys(ctx).forEach(k => {
    if (ctx[k] == null) delete ctx[k];
  });

  return ctx;
}

export { buildContext };